import { useState } from 'react';

import { Box, Button, VStack } from '@chakra-ui/react';
import {
  CallbackType,
  FRAuth,
  FRStep,
  TokenManager,
} from '@forgerock/javascript-sdk';
import { useRouter } from 'next/router';

import { Text } from '@/components';
import { Input, } from "@chakra-ui/react"
import { JOURNEY_LOGIN_EMAIL } from '@/constants/forgrock.constants';
import {
  seedAuthenticationSession,
  seedVendorAuthenticationSession,
} from '@/helpers/session';
import useTranslation from '@/hooks/useTranslate';
import colors from '@/styles/themes/brand/colors';
import style from './UserLogin.style';
import { CUSTOMERPORTAL_AUTH_INTENT_KEY, VENDOR_AUTH_INTENT_KEY } from '@/constants/storage.constants';

const MAX_STEPS = 10;

const UserLogin = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      return;
    }

    const customerLogin = localStorage.getItem(CUSTOMERPORTAL_AUTH_INTENT_KEY);
    const vendorLogin = localStorage.getItem(VENDOR_AUTH_INTENT_KEY);

    setError('');
    setIsLoading(true);

    try {
      let step = await FRAuth.start({
        tree: JOURNEY_LOGIN_EMAIL,
      });

      let stepCount = 0;

      while (stepCount < MAX_STEPS) {
        console.log(
          'ForgeRock step:',
          step.type
        );

        /*
         * ---------------------------------------------------------
         * 1. LOGIN SUCCESS
         * ---------------------------------------------------------
         */
        if (step.type === 'LoginSuccess') {
          console.log('ForgeRock login successful');

          await TokenManager.getTokens({
            forceRenew: true,
          });

          // middleware.ts gates /vendor/registration on the vendor cookie and
          // /dashboard on the customer one — seed whichever the redirect needs,
          // or it silently bounces back.
          if (customerLogin) {
            seedAuthenticationSession(true);
            await router.push('/dashboard');
          } else {
            seedVendorAuthenticationSession();
            await router.push('/vendor/registration');
          }

          return;
        }

        /*
         * ---------------------------------------------------------
         * 2. LOGIN FAILURE
         * ---------------------------------------------------------
         */
        if (step.type === 'LoginFailure') {
          console.error(
            'ForgeRock login failure:',
            step
          );

          setError(t('vendor_login_error'));

          return;
        }

        /*
         * ---------------------------------------------------------
         * 3. NORMAL AUTHENTICATION STEP
         * ---------------------------------------------------------
         */
        if (step.type === 'Step') {
          const frStep = step as FRStep;

          /*
           * -------------------------------------------------------
           * 3A. UAE PASS / EXTERNAL IdP REDIRECT
           * -------------------------------------------------------
           *
           * Do NOT try to fill username/password here.
           *
           * ForgeRock is telling the browser to go to UAE PASS.
           */
          const redirectCallbacks =
            frStep.getCallbacksOfType(
              CallbackType.RedirectCallback
            );

          if (redirectCallbacks.length > 0) {
            console.log(
              'Redirecting to UAE PASS'
            );

            await FRAuth.redirect(frStep);

            /*
             * Browser should navigate away.
             */
            return;
          }

          /*
           * -------------------------------------------------------
           * 3B. USERNAME
           * -------------------------------------------------------
           */
          const nameCallbacks =
            frStep.getCallbacksOfType(
              CallbackType.NameCallback
            );

          if (nameCallbacks.length > 0) {
            frStep.setCallbackValue(
              CallbackType.NameCallback,
              email
            );
          }

          /*
           * -------------------------------------------------------
           * 3C. PASSWORD
           * -------------------------------------------------------
           */
          const passwordCallbacks =
            frStep.getCallbacksOfType(
              CallbackType.PasswordCallback
            );

          if (passwordCallbacks.length > 0) {
            frStep.setCallbackValue(
              CallbackType.PasswordCallback,
              password
            );
          }

          /*
           * -------------------------------------------------------
           * 3D. SEND STEP BACK TO FORGEROCK
           * -------------------------------------------------------
           */
          step = await FRAuth.next(frStep, {
            tree: JOURNEY_LOGIN_EMAIL,
          });

          stepCount++;

          continue;
        }

        /*
         * ---------------------------------------------------------
         * UNKNOWN STEP
         * ---------------------------------------------------------
         */
        console.warn(
          'Unhandled ForgeRock step:',
          step
        );

        break;
      }

      setError(t('vendor_login_error'));
    } catch (err) {
      console.error(
        'ForgeRock login failed:',
        err
      );

      setError(t('vendor_login_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const isLoginDisabled =
    isLoading ||
    !email ||
    !password;

  return (
    <Box
      sx={{
        width: '100%',
        py: 10,
      }}
    >
      <VStack
        spacing={5}
        alignItems="stretch"
      >
        <Input
          sx={style.textInput}
          placeholder="Email"
          id="userLoginEmail"
          type="email"
          autoComplete="username"
          autoCapitalize="none"
          value={email}
          onChange={(e) =>
            setEmail(e.target.value)
          }
        />

        <Input

          sx={style.textInput}
          id="userLoginPassword"
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) =>
            setPassword(e.target.value)
          }
        />

        {error ? (
          <Text
            as="span"
            color={colors.error}
            fontSize="sm"
          >
            {error}
          </Text>
        ) : null}

        <Button
          isLoading={isLoading}
          disabled={isLoginDisabled}
          variant={'primary'}
          onClick={handleLogin}
        >
          {t('vendor_login_submit_label')}
        </Button>
      </VStack>
    </Box>
  );
};

export default UserLogin;
